<template>
  <div
    class="guest-signature"
    :class="{
      'theme-ecruise': $store.state.UI === 'ecruise',
      'guest-signature--warning': warning && !completed
    }"
    tabindex="-1"
    @pointerdown="onActivity"
    @wheel.passive="onActivity"
  >
    <div class="guest-signature__brand">
      MARGARITAVILLE AT SEA

      <button
        class="crew-escape"
        type="button"
        aria-label=""
        @pointerdown.stop="onCrewTap"
      ></button>

      <button
        v-if="!completed"
        class="guest-signature__exit"
        type="button"
        aria-label="Close signing screen"
        title="Close signing screen"
        :disabled="busy"
        @click="requestExit"
      >&times;</button>
    </div>

    <div v-if="warning && !completed" class="guest-signature__warning" role="alert">
      <strong>Session ending soon.</strong>
      Returning to login in {{ secondsUntilTimeout }}s due to inactivity.
      Tap the screen to continue. Your check remains open.
    </div>

    <div v-if="completed" class="guest-signature__confirmation">
      <div class="guest-signature__confirmation-card">
        <div class="guest-signature__success" aria-hidden="true">✓</div>
        <h1>Thank You!</h1>
        <p>Your check has been closed.</p>

        <div class="guest-signature__receipt">
          <div
            v-for="(item, index) in order.items"
            :key="item.id || index"
            class="guest-signature__item"
          >
            <span>{{ item.count }}× {{ item.name }}</span>
            <strong>${{ (Number(item.total ?? item.price * item.count) || 0).toFixed(2) }}</strong>
          </div>
          <div class="guest-signature__receipt-row">
            <span>Order Total</span><span>${{ baseTotal.toFixed(2) }}</span>
          </div>
          <div v-if="tipAmount > 0" class="guest-signature__receipt-row">
            <span>Additional Tip</span><span>${{ tipAmount.toFixed(2) }}</span>
          </div>
          <div class="guest-signature__receipt-total">
            <span>Total Charged</span>
            <strong>${{ (confirmedTotal ?? totalWithTip).toFixed(2) }}</strong>
          </div>
          <div v-if="payers.length" class="guest-signature__receipt-row">
            <span>Charged to</span><span>{{ payerDescription }}</span>
          </div>
        </div>

        <p v-if="printError" class="guest-signature__error" role="alert">
          {{ printError }}
        </p>
        <button type="button" @click="$emit('done')">Done</button>
        <p class="guest-signature__returning">
          Returning to login in {{ secondsToLogout }}s
        </p>
      </div>
    </div>

    <div v-else class="guest-signature__content">
      <div class="guest-signature__layout">
        <h1 class="guest-signature__heading">Review and sign your check</h1>

        <section class="guest-signature__items" aria-label="Order items">
          <h2>Your Order</h2>
          <div class="guest-signature__item-list">
            <div
              v-for="(item, index) in order.items"
              :key="item.id || index"
              class="guest-signature__item"
            >
              <span>{{ item.count }} × {{ item.name }}</span>
              <span>${{ (Number(item.total ?? item.price * item.count) || 0).toFixed(2) }}</span>
            </div>
          </div>
          <div class="guest-signature__current-total">
            <span>Total</span><strong>${{ baseTotal.toFixed(2) }}</strong>
          </div>
          <p
            v-if="Number(order.auto_gratuity_amount) > 0"
            class="guest-signature__auto-gratuity"
          >
            Includes
            {{ order.auto_gratuity_percent
              ? `${order.auto_gratuity_percent}%`
              : `$${Number(order.auto_gratuity_amount).toFixed(2)}` }}
            auto gratuity
          </p>
        </section>

        <section class="guest-signature__form" aria-label="Additional tip and signature">
          <label class="guest-signature__tip-label" for="guest-tip">
            Additional Tip <span>(optional)</span>
          </label>
          <div class="guest-signature__tip-row">
            <input
              id="guest-tip"
              class="guest-signature__tip-input"
              type="text"
              inputmode="none"
              readonly
              :value="tipValue || '0.00'"
            />
            <div class="guest-signature__tip-type">
              <button
                type="button"
                :class="{ selected: tipType === 'amount' }"
                @click="setTipType('amount')"
              >$</button>
              <button
                type="button"
                :class="{ selected: tipType === 'percentage' }"
                @click="setTipType('percentage')"
              >%</button>
            </div>
          </div>
          <keypad
            v-show="!showCrewOverride && !showExitConfirmation"
            @clickOnKey="appendTip"
            @clear="removeTip"
          />

          <div class="guest-signature__summary">
            <div>
              <span>Order Total</span><span>${{ baseTotal.toFixed(2) }}</span>
            </div>
            <div v-if="tipAmount > 0">
              <span>Additional Tip</span><span>${{ tipAmount.toFixed(2) }}</span>
            </div>
            <div class="guest-signature__total">
              <span>New Total</span><strong>${{ totalWithTip.toFixed(2) }}</strong>
            </div>
          </div>

          <div class="guest-signature__signature-heading">
            <label for="guest-signature-canvas">Signature</label>
            <button
              v-if="signed"
              type="button"
              class="guest-signature__clear"
              :disabled="busy"
              @click="clearSignature"
            >Clear</button>
          </div>
          <div class="guest-signature__canvas-wrapper" :class="{ signed }">
            <canvas
              id="guest-signature-canvas"
              ref="signatureCanvas"
              aria-label="Sign here with your finger"
              @pointerdown="startDrawing"
              @pointermove="draw"
              @pointerup="stopDrawing"
              @pointercancel="stopDrawing"
            ></canvas>
            <span v-if="!signed" class="guest-signature__placeholder">
              Sign here with your finger
            </span>
          </div>

          <p v-if="error" class="guest-signature__error" role="alert">
            {{ error }}
          </p>
          <button
            type="button"
            class="guest-signature__confirm"
            :disabled="!signed || busy"
            @click="confirm"
          >
            {{ busy
              ? 'Processing…'
              : signed
                ? `Confirm — $${totalWithTip.toFixed(2)}`
                : 'Sign to Confirm' }}
          </button>
        </section>
      </div>
    </div>

    <div
      v-if="showCrewOverride"
      class="guest-signature__overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Crew Override"
    >
      <div class="guest-signature__override">
        <h2>Crew Override</h2>
        <p>Skip signature and close the check?</p>
        <small>You will need to log in again after this action.</small>

        <p
          v-if="error"
          class="guest-signature__override-error"
          role="alert"
        >
          {{ error }}
        </p>

        <div>
          <button
            type="button"
            :disabled="busy"
            @click="showCrewOverride = false"
          >
            Go Back
          </button>
          <button
            type="button"
            :disabled="busy"
            @click="skipSignature"
          >
            {{ busy ? 'Closing…' : 'Skip & Close' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showExitConfirmation"
      class="guest-signature__overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Close signing screen"
    >
      <div class="guest-signature__override">
        <h2>Close signing screen?</h2>
        <p>Your signature and additional tip will be discarded. The check will stay open.</p>
        <small>A crew member will need to log in again.</small>
        <div>
          <button type="button" @click="showExitConfirmation = false">
            Keep Signing
          </button>
          <button type="button" @click="leaveSigningScreen">
            Return to Login
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Keypad from '@/components/_shared/Keypad.vue';
import { constants } from '@/constants/constants';

export default {
  name: 'GuestSignatureFlow',
  components: { Keypad },
  props: {
    order: { type: Object, required: true },
    payers: { type: Array, required: true },
  },
  emits: ['confirm', 'skip', 'timeout', 'done', 'cancel'],

  data() {
    return {
      tipType: this.order.gratuity_type === constants.gratuityTypes.percentage
        ? 'percentage'
        : 'amount',
      tipValue: String(
        this.order.gratuity_type === constants.gratuityTypes.percentage
          ? this.order.gratuity_percent ?? 0
          : this.order.gratuity_amount ?? 0
      ),
      signed: false,
      drawing: false,
      busy: false,
      completed: false,
      warning: false,
      secondsUntilTimeout: 30,
      showCrewOverride: false,
      showExitConfirmation: false,
      secondsToLogout: 15,
      error: '',
      printError: '',
      confirmedTotal: null,
      lastActivity: Date.now(),
      crewTaps: [],
      timer: null,
      doneTimer: null,
    };
  },

  computed: {
    currentTotal() {
      return Number(this.order.total ?? this.order.subtotal ?? 0);
    },

    existingTip() {
      return Number(this.order.gratuity_amount ?? 0);
    },

    baseTotal() {
      return Math.max(0, this.currentTotal - this.existingTip);
    },

    tipAmount() {
      const value = Number(this.tipValue) || 0;

      if (this.tipType === 'amount') return value;

      const percentageBase = Number(this.order.manual_gratuity_base ?? this.order.subtotal ?? 0);
      return Math.round(percentageBase * value) / 100;
    },

    totalWithTip() {
      return this.baseTotal + this.tipAmount;
    },

    payerDescription() {
      if (this.payers.length > 1) {
        return `${this.payers.length} payers`;
      }

      if (this.payers[0]?.cabin) {
        return `Cabin ${this.payers[0].cabin}`;
      }

      return 'Crew charge';
    },
  },

  mounted() {
    this.$nextTick(this.resizeCanvas);
    this.$el.focus();

    this.timer = window.setInterval(this.checkIdle, 1000);
    window.addEventListener('keydown', this.onKeydown, true);
    window.addEventListener('beforeunload', this.onUnload);
  },

  beforeUnmount() {
    window.clearInterval(this.timer);
    window.clearTimeout(this.doneTimer);
    window.removeEventListener('keydown', this.onKeydown, true);
    window.removeEventListener('beforeunload', this.onUnload);
    this.$store.dispatch('setGuestFlowActive', false);
  },

  methods: {
    onUnload() {
      localStorage.removeItem('ud');
      localStorage.removeItem('posData');
    },

    onKeydown(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.onActivity();
    },

    onActivity() {
      if (this.busy || this.completed) return;

      this.lastActivity = Date.now();
      this.warning = false;
    },

    checkIdle() {
      if (this.completed) {
        this.secondsToLogout = Math.max(
          0,
          Math.ceil((this.returnAt - Date.now()) / 1000)
        );
        return;
      }

      if (this.busy) return;

      const elapsed = Date.now() - this.lastActivity;

      if (elapsed >= 90000) {
        this.$emit('timeout');
      } else if (elapsed >= 60000) {
        this.secondsUntilTimeout = Math.ceil(
          (90000 - elapsed) / 1000
        );
        this.warning = true;
      }
    },

    onCrewTap() {
      if (this.busy || this.completed) return;

      this.onActivity();

      const now = Date.now();
      this.crewTaps = this.crewTaps.filter(time => now - time < 800);
      this.crewTaps.push(now);

      if (this.crewTaps.length >= 3) {
        this.crewTaps = [];
        this.error = '';
        this.showCrewOverride = true;
      }
    },

    requestExit() {
      if (this.busy || this.completed) return;

      this.onActivity();
      this.showExitConfirmation = true;
    },

    leaveSigningScreen() {
      if (this.busy || this.completed || !this.showExitConfirmation) return;

      this.$emit('cancel');
    },

    skipSignature() {
      if (this.busy || this.completed || !this.showCrewOverride) return;

      this.busy = true;
      this.error = '';
      this.$emit('skip');
    },

    setTipType(type) {
      if (this.busy || this.tipType === type) return;

      this.tipType = type;
      this.tipValue = '';
      this.onActivity();
    },

    appendTip(key) {
      if (this.busy) return;
      if (key === '.' && this.tipValue.includes('.')) return;

      const next = `${this.tipValue}${key}`;

      if (!/^\d{0,6}(\.\d{0,2})?$/.test(next)) return;
      if (this.tipType === 'percentage' && Number(next) > 100) return;

      this.tipValue = next;
      this.onActivity();
    },

    removeTip() {
      if (this.busy) return;

      this.tipValue = this.tipValue.slice(0, -1);
      this.onActivity();
    },

     resizeCanvas() {
      const canvas = this.$refs.signatureCanvas;

      if (!canvas) return;

      const { width, height } = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);

      const context = canvas.getContext('2d');
      context.scale(ratio, ratio);
      context.strokeStyle = '#2c3e50';
      context.lineWidth = 3;
      context.lineCap = 'round';
      context.lineJoin = 'round';

      this.signed = false;
    },

    position(event) {
      const rect = this.$refs.signatureCanvas.getBoundingClientRect();

      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    },

    startDrawing(event) {
      if (this.busy) return;

      event.preventDefault();

      const canvas = this.$refs.signatureCanvas;
      canvas.setPointerCapture(event.pointerId);

      const { x, y } = this.position(event);
      const context = canvas.getContext('2d');

      context.beginPath();
      context.moveTo(x, y);

      this.drawing = true;
    },

    draw(event) {
      if (!this.drawing) return;

      event.preventDefault();

      const { x, y } = this.position(event);
      const context = this.$refs.signatureCanvas.getContext('2d');

      context.lineTo(x, y);
      context.stroke();

      this.signed = true;
    },

    stopDrawing() {
      this.drawing = false;
    },

    clearSignature() {
      if (this.busy) return;

      const canvas = this.$refs.signatureCanvas;

      canvas.getContext('2d').clearRect(
        0,
        0,
        canvas.width,
        canvas.height
      );

      this.signed = false;
      this.onActivity();
    },

    confirm() {
      if (!this.signed || this.busy) return;

      this.busy = true;
      this.error = '';

      const value = Number(this.tipValue) || 0;
      const isPercentage = this.tipType === 'percentage' && value > 0;

      this.$emit('confirm', {
        user_profile_id:
          this.payers[0]?.id
          ?? null,
        signature_data: this.$refs.signatureCanvas.toDataURL('image/png'),
        gratuity_type: isPercentage
          ? constants.gratuityTypes.percentage
          : constants.gratuityTypes.amount,
        gratuity_amount: isPercentage ? null : value,
        gratuity_percent: isPercentage ? value : null,
      });
    },

    fail(message) {
      this.busy = false;
      this.error = message;
      this.onActivity();
    },

    complete(printError = '', total = null) {
      this.completed = true;
      this.printError = printError;
      this.confirmedTotal = total === null ? null : Number(total);
      this.doneTimer = window.setTimeout(() => this.$emit('done'), 15000);
    },
  },
};
</script>

<style scoped lang="less">
.guest-signature {
  --accent: #01abc0;
  --accent-dark: #017e8d;
  position: fixed;
  z-index: 100000;
  inset: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  box-sizing: border-box;
  background: #f5f9fc;
  color: #1c282d;
  overscroll-behavior: contain;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;

  &.theme-ecruise {
    --accent: #3f8ec5;
    --accent-dark: #2873a8;
  }

  &, * { font-family: 'OS', sans-serif; }
  button { cursor: pointer; }

  &__brand {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    background: var(--accent);
    color: white;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 1px;
    text-align: center;
  }

  .crew-escape {
    position: absolute;
    top: 0;
    left: 0;
    width: 80px;
    height: 48px;
    border: 0;
    background: transparent;
  }

  &__exit {
    position: absolute;
    top: 0;
    right: 6px;
    width: 48px;
    height: 48px;
    padding: 0;
    border: 0;
    background: transparent;
    color: white;
    font-size: 30px;
    line-height: 1;
  }

  &__exit:disabled {
    opacity: .5;
    cursor: default;
  }

  &__content, &__confirmation-card {
    width: calc(100% - 20px);
    max-width: 680px;
    margin: 6px auto 10px;
    overflow: hidden;
    box-sizing: border-box;
    border-radius: 14px;
    background: white;
    box-shadow: 0 6px 28px rgba(28, 40, 45, .13);
  }

  &__layout { padding: 14px 22px 18px; }

  &__heading {
    margin: 0 0 8px;
    color: #1c282d;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
    text-align: center;
  }

  &__items {
    padding-bottom: 7px;
    margin-bottom: 9px;
    border-bottom: 1px solid #d9e8f3;
  }

  &__item-list {
    max-height: 12vh;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  &__items h2, &__tip-label, &__signature-heading label {
    display: block;
    margin: 0 0 4px;
    color: #465c68;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    padding: 3px 0;
    color: #1c282d;
    font-size: 16px;
    text-align: left;
  }

  &__item span:last-child {
    white-space: nowrap;
    font-weight: 600;
  }

  &__current-total {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
    padding-top: 5px;
    border-top: 1px solid #d9e8f3;
    font-size: 18px;
    font-weight: 700;
  }

  &__auto-gratuity {
    margin: 4px 0 0;
    color: #546e7a;
    font-size: 13px;
    text-align: center;
  }

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__tip-label { order: 0; }
  &__tip-label span { font-weight: 400; }

  &__tip-row {
    order: 1;
    display: flex;
    gap: 8px;
    margin-bottom: 7px;
  }

  &__tip-input {
    flex: 1;
    min-width: 0;
    height: 48px;
    box-sizing: border-box;
    padding: 0 16px;
    border: 2px solid var(--accent);
    border-radius: 10px;
    background: white;
    color: #1c282d;
    font-size: 21px;
    font-weight: 700;
  }

  &__tip-type {
    display: flex;
    overflow: hidden;
    border: 2px solid var(--accent);
    border-radius: 10px;
  }

  &__tip-type button {
    min-width: 48px;
    border: 0;
    background: white;
    color: var(--accent-dark);
    font-size: 18px;
    font-weight: 700;
  }

  &__tip-type button.selected {
    background: var(--accent);
    color: white;
  }

  &__form :deep(.keypad) {
    order: 2;
    z-index: 0;
    width: 100%;
    max-width: 320px;
    height: auto;
    margin: 0 auto 8px;
    border: 0;
    background: transparent;
  }

  &__form :deep(.keypad .flex-row) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 5px;
    padding: 0;
  }

  &__form :deep(.keypad .number) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: none;
    height: clamp(38px, 5.5vh, 47px);
    min-height: 38px;
    margin: 0;
    border: 1px solid #d9e8f3;
    border-radius: 9px;
    background: white;
    color: #1c282d;
    box-shadow: 0 2px 0 var(--accent);
    font-size: 22px;
    line-height: 1;
  }

  &__form :deep(.keypad .clear) {
    background: white;
    box-shadow: none;
  }

  &__summary {
    order: 3;
    padding: 8px 12px;
    margin-bottom: 9px;
    border-radius: 10px;
    background: #f5f9fc;
  }

  &__summary > div, &__receipt-row, &__receipt-total {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 2px 0;
    color: #465c68;
    font-size: 14px;
  }

  &__total, &__receipt-total {
    margin-top: 5px;
    padding-top: 7px !important;
    border-top: 1px solid #d9e8f3;
    color: #1c282d !important;
    font-size: 18px !important;
    font-weight: 700;
  }

  &__signature-heading {
    order: 4;
    position: relative;
  }

  &__signature-heading label { margin-bottom: 4px; }

  &__clear {
    position: absolute;
    z-index: 1;
    top: 22px;
    right: 6px;
    padding: 4px 12px;
    border: 0;
    border-radius: 7px;
    background: #f0f5f8;
    color: var(--accent-dark);
    font-size: 14px;
    font-weight: 700;
  }

  &__canvas-wrapper {
    order: 5;
    position: relative;
    overflow: hidden;
    border: 2px dashed #b6c9da;
    border-radius: 10px;
    background: white;
  }

  &__canvas-wrapper.signed {
    border-color: var(--accent);
    border-style: solid;
  }

  canvas {
    display: block;
    width: 100%;
    height: clamp(96px, 13vh, 118px);
    touch-action: none;
  }

  &__placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    color: #8798a3;
    font-size: 16px;
  }

  &__confirm {
    order: 7;
    position: sticky;
    bottom: 10px;
    z-index: 1;
    width: 100%;
    min-height: 54px;
    margin-top: 10px;
    padding: 9px;
    border: 0;
    border-radius: 10px;
    background: var(--accent);
    color: white;
    font-size: 17px;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__confirm:disabled {
    background: #e4ebf1;
    color: #647987;
    cursor: default;
  }

  &__error {
    order: 6;
    margin: 10px 0 0;
    color: #b8362a;
    font-size: 15px;
  }

  &__warning {
    position: sticky;
    z-index: 10;
    top: 48px;
    width: min(680px, calc(100vw - 20px));
    box-sizing: border-box;
    margin: 0 auto;
    padding: 9px 18px;
    border: 1px solid #e6be58;
    border-radius: 0 0 12px 12px;
    background: #fff3cd;
    box-shadow: 0 5px 18px rgba(28, 40, 45, .2);
    color: #6a4800;
    text-align: center;
    font-size: 16px;
    line-height: 1.4;
  }

  &--warning {
    .guest-signature__layout {
      padding: 10px 20px 12px;
    }

    .guest-signature__heading {
      margin-bottom: 6px;
      font-size: 22px;
    }

    .guest-signature__items {
      padding-bottom: 6px;
      margin-bottom: 8px;
    }

    .guest-signature__tip-input {
      height: 44px;
    }

    .guest-signature__form :deep(.keypad .number) {
      height: clamp(38px, 5.5vh, 44px);
      font-size: 20px;
    }

    .guest-signature__summary {
      padding: 6px 12px;
      margin-bottom: 8px;
    }

    .guest-signature__confirm {
      min-height: 50px;
      margin-top: 8px;
    }
  }

  &__confirmation-card {
    max-width: 620px;
    padding: 30px;
    text-align: center;
  }

  &__success {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    margin: 0 auto 14px;
    border-radius: 50%;
    background: #27ae60;
    color: white;
    font-size: 40px;
  }

  &__confirmation h1 {
    margin: 0 0 4px;
    font-size: 27px;
  }

  &__confirmation p {
    margin: 0 0 18px;
    color: #465c68;
    font-size: 16px;
  }

  &__receipt {
    padding: 18px;
    margin-bottom: 20px;
    border-radius: 10px;
    background: #f5f9fc;
    text-align: left;
  }

  &__receipt .guest-signature__item {
    font-size: 16px;
  }

  &__confirmation-card button {
    width: 100%;
    min-height: 60px;
    border: 0;
    border-radius: 10px;
    background: var(--accent);
    color: white;
    font-size: 19px;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__confirmation .guest-signature__returning {
    margin: 12px 0 0;
    color: #647987;
    font-size: 14px;
  }

  &__overlay {
    position: fixed;
    z-index: 3000;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    background: rgba(0, 0, 0, .5);
  }

  &__override {
    width: 100%;
    max-width: 440px;
    box-sizing: border-box;
    padding: 28px;
    border-radius: 12px;
    background: white;
    box-shadow: 0 6px 28px rgba(28, 40, 45, .18);
    text-align: center;
  }

  &__override h2 {
    margin: 0 0 8px;
    font-size: 23px;
  }

  &__override p {
    margin: 0 0 7px;
    font-size: 17px;
  }

  &__override small {
    color: #546e7a;
    font-size: 14px;
  }

  &__override .guest-signature__override-error {
    margin: 12px 0 0;
    color: #b8362a;
    font-size: 15px;
  }

  &__override > div {
    display: flex;
    gap: 10px;
    margin-top: 22px;
  }

  &__override button {
    flex: 1;
    min-height: 54px;
    padding: 8px;
    border: 0;
    border-radius: 9px;
    background: #e9eef2;
    color: #1c282d;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
  }

  &__override button:last-child {
    background: #e86d32;
    color: white;
  }

  &__override button:disabled {
    opacity: .6;
    cursor: default;
  }

  &.theme-ecruise .guest-signature__override button:last-child {
    background: #eb262b;
  }

  @media (max-width: 600px) {
    &__brand {
      height: 46px;
      font-size: 16px;
    }

    .crew-escape { height: 46px; }
    &__exit { height: 46px; }

    &__content, &__confirmation-card {
      width: calc(100% - 12px);
      margin: 4px auto 8px;
    }

    &__layout { padding: 8px 12px 10px; }
    &__heading { font-size: 20px; }
    &__tip-type button { min-width: 44px; }

    &__warning {
      top: 46px;
      width: calc(100vw - 12px);
      font-size: 15px;
    }

    &__confirmation-card {
      padding: 22px 16px;
    }

    &--warning .guest-signature__layout {
      padding: 8px 12px 10px;
    }

    &--warning .guest-signature__heading {
      font-size: 20px;
    }
  }

  @media (max-height: 650px) {
    &__brand { height: 44px; }
    .crew-escape { height: 44px; }
    &__exit { height: 44px; }
    &__warning { top: 44px; }

    &__layout {
      padding-top: 7px;
      padding-bottom: 8px;
    }

    &__heading {
      font-size: 19px;
      margin-bottom: 4px;
    }

    &__item-list { max-height: 10vh; }
    &__tip-input { height: 40px; }

    &__form :deep(.keypad .number) {
      height: 36px;
      min-height: 36px;
      font-size: 18px;
    }

    canvas { height: 74px; }

    &__confirm {
      min-height: 44px;
      font-size: 16px;
    }

    &--warning .guest-signature__layout {
      padding-top: 7px;
      padding-bottom: 8px;
    }

    &--warning .guest-signature__heading {
      font-size: 19px;
    }

    &--warning .guest-signature__tip-input {
      height: 40px;
    }

    &--warning .guest-signature__form :deep(.keypad .number) {
      height: 36px;
      min-height: 36px;
      font-size: 18px;
    }

    &--warning .guest-signature__confirm {
      min-height: 44px;
    }
  }
}
</style>